import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { ferryBookingToWizardData } from 'Util/_HumanWritten/BookingWizard/FerryBookingToWizardDataUtils';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { BookingEntity } from 'Models/Entities';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { feeType, wizardModeOptions } from 'Models/Enums';
import { removeGiftCertificateFromTransaction } from 'Services/Api/_HumanWritten/GiftCertificateService';
import alertToast from 'Util/ToastifyUtils';
import If from 'Views/Components/If/If';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import TripBookingSummaryInner from './TripBookingSummaryInner';
import { tripSummaryLocationType } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/TripBookingSummaryCard';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { addManagerDiscount, removeManagerDiscount } from 'Services/Api/_HumanWritten/ManagerBookingDiscountService';
import {
	fetchBookingsForCart,
	removeFee,
	removeFeeFromTransaction,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { removeDiscountFromTransaction } from 'Services/Api/_HumanWritten/DiscountService';
import { TermsAndConditionsType } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/TermsAndConditions/TermsAndConditions';
import { transactionPricingQueryOptions } from 'Services/Api/_HumanWritten/PricingService/PricingService';

export interface CartSummaryProps {
	wizardData: BookingWizardData;
	selectedTrips: SelectedTrips;
	onUpdateWizardData: (newData: BookingWizardData) => void;
	setSelectedTrips: (trips: SelectedTrips) => void;
	setTermsAndConditionsTypes: (terms: TermsAndConditionsType[]) => void;
	setRefresh: (refresh: boolean) => void;
	refresh: boolean;
	tripSummaryLocation: tripSummaryLocationType;
	setTabIsLoading?: (isLoading: boolean) => void;
}

export function CartSummary({
	wizardData,
	selectedTrips,
	onUpdateWizardData,
	setSelectedTrips,
	setTermsAndConditionsTypes,
	setTabIsLoading,
}: CartSummaryProps) {
	const [bookingList, setBookingList] = useState<BookingEntity[]>([]);
	const [refetch, setRefetch] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const transactionId = getFerryBookingTransactionIdFromStorage();

	const fetchBookings = async (): Promise<BookingEntity[]> => {
		if (!transactionId) {
			setBookingList([]);
			setIsLoading(false);
			return [];
		}

		try {
			setIsLoading(true);
			setError(null);
			const bookings = await fetchBookingsForCart(transactionId);
			setBookingList(bookings);

			const termsAndConditionTypes: TermsAndConditionsType[] = [];
			bookings.forEach(booking => {
				if (!!booking.bookingSummaries.recentSummary.cargoInfo) {
					termsAndConditionTypes.push('vehicle');
				} else if (!!booking.bookingSummaries.recentSummary.freightDetail) {
					termsAndConditionTypes.push('freight');
				} else {
					termsAndConditionTypes.push('passenger');
				}
			});
			setTermsAndConditionsTypes(Array.from(new Set(termsAndConditionTypes)));

			// Check if wizard data needs to be populated with a valid booking
			// In case the user half completed a booking and then clicked on the cart icon
			// (meaning there is half completed wizard data which can result in being thrown back to the tickets tab
			// when trying to complete payment in the booking wizard).
			if (bookings.length > 0 && !wizardData.bookingToEdit) {
				// Find the first booking to use
				const firstBooking = bookings[0];

				// Convert the booking to wizard data
				const reconstructedCartFields = ferryBookingToWizardData(
					[firstBooking],
					undefined,
					undefined,
					wizardData.wizardMode === 'ALTERATION',
					!(firstBooking.returnBooking ?? firstBooking.returnBookingForId),
				);

				if (reconstructedCartFields?.[0]) {
					const newData = new BookingWizardData({ ...reconstructedCartFields[0].wizardData });
					onUpdateWizardData(newData);
				}
			}

			if (setTabIsLoading) {
				setTabIsLoading(false);
			}

			return bookings; // Return the bookings
		} catch (err) {
			setError(err as Error);
			console.error('Error fetching bookings:', err);
			return [];
		} finally {
			setIsLoading(false);
			setIsInitialLoad(false);
		}
	};

	// Start the cart's pricing now, alongside fetch-bookings-for-cart, instead of once the summary below
	// mounts after it. The summary's first fetch takes this answer (see useFetchBookingPriceBreakdown);
	// same arguments as the cart's own fetch — no card fee, not check-in. staleTime 0 makes it a fresh
	// request each time the cart opens.
	const queryClient = useQueryClient();
	useEffect(() => {
		if (transactionId) {
			queryClient.prefetchQuery({ ...transactionPricingQueryOptions(transactionId, false, false), staleTime: 0 });
		}
	}, [transactionId, queryClient]);

	// Initial fetch and refetch on refetch state change
	useEffect(() => {
		fetchBookings();
	}, [refetch, transactionId, wizardData.userId]);

	const onClearGiftCertificate = (code: string) => {
		removeGiftCertificateFromTransaction(transactionId ?? '', code, false, false)
			.then(_ => {
				alertToast('Removed gift certificate', 'success');
				setRefetch(!refetch);
			})
			.catch(_ => {
				alertToast('Could not remove gift certificate', 'error');
			});
	};

	const onRemoveDiscount = (discountId: string) => {
		removeDiscountFromTransaction(transactionId ?? '', discountId, false, false)
			.then(_ => {
				alertToast('Removed discount', 'success');
				setRefetch(!refetch);
			})
			.catch(_ => {
				alertToast('Could not remove discount', 'error');
			});
	};

	const onClearFee = (feeId: string) => {
		removeFeeFromTransaction(transactionId ?? '', feeId, false, false)
			.then(_ => {
				setRefetch(!refetch);
			})
			.catch(_ => {
				alertToast('Could not remove fee', 'error');
			});
	};

	const onRemoveManagerDiscount = async () => {
		if (isNotNullOrUndefined(transactionId)) {
			try {
				await removeManagerDiscount({
					transactionId: transactionId,
				});
				setRefetch(!refetch);
			} catch {
				alertToast('Could not remove manager discount from transaction', 'error');
			}
		}
	};

	const onRemoveFee = async (
		feeToRemove: feeType,
		departingDiscountAmount?: number,
		returningDiscountAmount?: number,
	) => {
		if (isNotNullOrUndefined(transactionId)) {
			try {
				const newData = new BookingWizardData({ ...wizardData });
				if (feeToRemove === 'OTHER') {
					try {
						await addManagerDiscount({
							transactionId,
							departingDiscountAmount,
							returningDiscountAmount,
						});
						setRefetch(!refetch);
					} catch {
						alertToast('Could not add manager discount to transaction', 'error');
					}
				} else {
					await removeFee(transactionId, feeToRemove, false);
				}
				onUpdateWizardData(newData);
				setRefetch(!refetch);
			} catch {
				alertToast('Could not remove trailer removal fee from alteration', 'error');
			}
		}
	};

	if ((isLoading && isInitialLoad) || error) {
		return <LottieSpinner />;
	}

	const cartHeaderClass =
		wizardData.wizardMode === wizardModeOptions.ALTERATION
			? 'cart-summary-heading-alteration'
			: 'cart-summary-heading';

	return (
		<div className="cart-summary__container">
			<WizardStepHeader
				iconName="shopping_cart"
				title={<FormattedMessage id="cart-tab__title" defaultMessage="Review your cart" />}
				subtitle={
					<FormattedMessage
						id="cart-tab__subtitle"
						defaultMessage="Check your selections before continuing to payment."
					/>
				}
				showCloseLink={wizardData.wizardMode === wizardModeOptions.ALTERATION}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
				className={cartHeaderClass}
			/>
			<TripBookingSummaryInner
				tripSummaryLocation="cart"
				wizardData={wizardData}
				onUpdateWizardData={onUpdateWizardData}
				selectedTrips={selectedTrips}
				setSelectedTrips={setSelectedTrips}
				bookings={bookingList}
				refresh={fetchBookings}
				// Same fetch as `refresh`, handed to the voucher field so it can wait for the
				// totals rather than for the POST. Toggling `refetch` refreshes just as well
				// but cannot be awaited, so the spinner came down while the price was stale.
				onVoucherApplied={fetchBookings}
				refetch={refetch}
				setRefetch={setRefetch}
				onClearFee={feeId => onClearFee(feeId)}
				onClearGiftCertificate={code => onClearGiftCertificate(code)}
				onRemoveManagerDiscount={async () => {
					onRemoveManagerDiscount();
				}}
				onRemoveDiscount={discountId => onRemoveDiscount(discountId)}
				onRemoveFee={async (fee, departureDiscountAmount, returnDiscountAmount) => {
					onRemoveFee(fee, departureDiscountAmount, returnDiscountAmount);
				}}
			/>
		</div>
	);
}
